import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Search, Trash2, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare, LayoutGrid, Layers, Gauge, FileText, Clock, Check, PauseCircle, Package, Activity, Tag, TrendingDown, BellOff } from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useTranslation } from 'react-i18next';
import { getBrandLocale } from '../../shared/utils/brandCurrency';
import InventaireFormTabBar from './components/InventaireFormTabBar';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';

const AlertesSeuilsStockPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.alertesSeuilsStockPage.${key}`, opts);
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString(dateLocale) : null);
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  const stockAlertsDisabled = !inventoryConfig.alertLowStock && !inventoryConfig.alertOverstock;
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-alert-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-alert-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;

  const EMPTY_FORM = {
    product_id: '',
    product_name: '',
    product_code: '',
    current_stock: '',
    minimum_threshold: '',
    maximum_threshold: '',
    reorder_point: '',
    alert_type: 'low_stock',
    severity: 'warning',
    status: 'active',
    notes: '',
  };
  const [alertFormTab, setAlertFormTab] = useState('productStock');
  const alertFormTabs = useMemo(() => ([
    { id: 'productStock', label: p('section_product_stock'), icon: Package, iconColor: 'text-green-400' },
    { id: 'alertConfig', label: p('section_identification'), icon: Bell, iconColor: 'text-red-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_alerts');
  const [quickFilter, setQuickFilter] = useState('all');
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  useArticlesConfig();

  // Multi-column sorting state

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (stockAlertsDisabled) {
      void loadAlerts();
      return;
    }
    void autoCheckStockAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockAlertsDisabled]);

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch {
      setProducts([]);
    }
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const autoCheckStockAlerts = async () => {
    try {
      console.log('🔍 Auto-checking stock levels for alerts...');
      // First, trigger auto-check to generate alerts from low-stock products
      await localApiAdapter.post('/inventory/stock-alerts/auto-check');
      // Then load all alerts
      await loadAlerts();
    } catch (error) {
      console.error('Error auto-checking stock alerts:', error);
      // Even if auto-check fails, try to load existing alerts
      await loadAlerts();
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/stock-alerts');
      setAlerts(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading alerts:', error);
      setAlerts([]);
    }
  };

  const stats = {
    total: alerts.length,
    active: alerts.filter(a => a.status === 'active').length,
    acknowledged: alerts.filter(a => a.status === 'acknowledged').length,
    resolved: alerts.filter(a => a.status === 'resolved').length,
    critical: alerts.filter(a => a.severity === 'critical' && a.status === 'active').length,
    high: alerts.filter(a => a.severity === 'high' && a.status === 'active').length,
    low_stock: alerts.filter(a => a.alert_type === 'low_stock' && a.status === 'active').length
  };

  const mainTabs = [
    { id: 'all_alerts', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_severite', name: p('tab_by_severity'), icon: AlertTriangle },
    { id: 'par_type', name: p('tab_by_type'), icon: Tag }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: Bell },
    { id: 'acknowledged', name: p('filter_acknowledged'), icon: CheckCircle },
    { id: 'critical', name: p('filter_critical'), icon: AlertTriangle }
  ];

  const alertDistribution = useMemo(() => ({
    all_alerts: {
      title: p('dist_overview_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { id: 'acknowledged', label: p('dist_acknowledged'), count: stats.acknowledged, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { id: 'resolved', label: p('dist_resolved'), count: stats.resolved, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'critical', label: p('dist_critical'), count: stats.critical, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    },
    par_severite: {
      title: p('dist_by_severity_title'),
      data: [
        { id: 'critical', label: p('dist_severity_critical'), count: alerts.filter(a => a.severity === 'critical').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { id: 'high', label: p('dist_severity_high'), count: alerts.filter(a => a.severity === 'high').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'warning', label: p('dist_severity_warning'), count: alerts.filter(a => a.severity === 'warning').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { id: 'info', label: p('dist_severity_info'), count: alerts.filter(a => a.severity === 'info').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [
        { id: 'low_stock', label: p('dist_type_low_stock'), count: alerts.filter(a => a.alert_type === 'low_stock').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { id: 'high_stock', label: p('dist_type_high_stock'), count: alerts.filter(a => a.alert_type === 'high_stock').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { id: 'reorder_point', label: p('dist_type_reorder_point'), count: alerts.filter(a => a.alert_type === 'reorder_point').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { id: 'out_of_stock', label: p('dist_type_out_of_stock'), count: alerts.filter(a => a.alert_type === 'out_of_stock').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    }
  }), [t, stats, alerts]);

  const distributionInfo = alertDistribution[mainViewMode] || alertDistribution.all_alerts;

  const baseFilteredAlerts = alerts.filter(a => {
    if (!inventaireRecordInDateRange(dateRange, a.triggered_date, a.created_at)) return false;

    if (searchTerm && !a.alert_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !a.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !a.product_code?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_alerts') {
      if (quickFilter === 'active' && a.status !== 'active') return false;
      if (quickFilter === 'acknowledged' && a.status !== 'acknowledged') return false;
      if (quickFilter === 'critical' && (a.severity !== 'critical' || a.status !== 'active')) return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_alerts':
          if (activeDistributionFilter === 'active' && a.status !== 'active') return false;
          if (activeDistributionFilter === 'acknowledged' && a.status !== 'acknowledged') return false;
          if (activeDistributionFilter === 'resolved' && a.status !== 'resolved') return false;
          if (activeDistributionFilter === 'critical' && (a.severity !== 'critical' || a.status !== 'active')) return false;
          break;
        case 'par_severite':
          if (activeDistributionFilter === 'critical' && a.severity !== 'critical') return false;
          if (activeDistributionFilter === 'high' && a.severity !== 'high') return false;
          if (activeDistributionFilter === 'warning' && a.severity !== 'warning') return false;
          if (activeDistributionFilter === 'info' && a.severity !== 'info') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'low_stock' && a.alert_type !== 'low_stock') return false;
          if (activeDistributionFilter === 'high_stock' && a.alert_type !== 'high_stock') return false;
          if (activeDistributionFilter === 'reorder_point' && a.alert_type !== 'reorder_point') return false;
          if (activeDistributionFilter === 'out_of_stock' && a.alert_type !== 'out_of_stock') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('alert_number'),
    textColumnRule('product_name'),
    textColumnRule('current_stock', (r) => String(r.current_stock ?? '')),
    exactColumnRule('alert_type', (r) => r.alert_type ?? r.type),
    exactColumnRule('severity', (r) => r.severity),
    exactColumnRule('status', (r) => r.status),
  ], []);

  const filteredAlerts = useMemo(
    () => applyColumnFilters(baseFilteredAlerts, colFilters, columnFilterRules),
    [baseFilteredAlerts, colFilters, columnFilterRules],
  );
  const visibleAlerts = useMemo(
    () => sliceInventaireListItems(filteredAlerts, itemsPerPage),
    [filteredAlerts, itemsPerPage],
  );
  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/stock-alerts/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التنبيه' }));
        loadAlerts();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/stock-alerts/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'التنبيه' }));
        setSelectedRecords([]);
        loadAlerts();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error?.message }));
      }
    }
  };

  const filteredAlertIds = useMemo(() => filteredAlerts.map((a) => a.id), [filteredAlerts]);
  const allRowsSelected = filteredAlertIds.length > 0 && filteredAlertIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredAlertIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((aId) => aId !== id) : [...prev, id]));
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setAlertFormTab('productStock');
    setShowModal(false);
  };

  const handleProductSelect = (productId) => {
    const product = products.find((prod) => String(prod.id) === String(productId));
    setFormData((prev) => ({
      ...prev,
      product_id: productId || '',
      product_name: product?.name || prev.product_name,
      product_code: product?.reference || product?.code || prev.product_code,
      current_stock: product?.stock != null ? String(product.stock) : prev.current_stock,
    }));
  };

  const openNewAlertModal = () => {
    if (stockAlertsDisabled) {
      window.alert(p('notice_alerts_disabled', { defaultValue: 'تنبيهات المخزون معطّلة من إعدادات المخزون ← إعادة الطلب.' }));
      return;
    }
    setEditingRecord(null);
    setFormData({
      ...EMPTY_FORM,
      minimum_threshold: String(inventoryConfig.lowStockThreshold ?? ''),
      maximum_threshold: String(inventoryConfig.overstockThreshold ?? ''),
      reorder_point: String(inventoryConfig.reorderPoint ?? ''),
    });
    setAlertFormTab('productStock');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_name.trim()) {
      window.alert(p('label_product'));
      return;
    }
    try {
      setSaving(true);
      const payload = {
        product_id: formData.product_id || null,
        product_name: formData.product_name.trim(),
        product_code: formData.product_code || null,
        current_stock: parseFloat(formData.current_stock) || 0,
        minimum_threshold: parseFloat(formData.minimum_threshold) || 0,
        maximum_threshold: parseFloat(formData.maximum_threshold) || 0,
        reorder_point: parseFloat(formData.reorder_point) || 0,
        alert_type: formData.alert_type || 'low_stock',
        severity: formData.severity || 'warning',
        status: formData.status || 'active',
        notes: formData.notes || null,
        auto_generated: false,
      };
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/stock-alerts/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التنبيه' }));
      } else {
        await localApiAdapter.post('/inventory/stock-alerts', payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التنبيه' }));
      }
      resetForm();
      await loadAlerts();
    } catch (error) {
      console.error('Error saving alert:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      return;
    } finally {
      setSaving(false);
    }
  };

  const alertTypeOptions = useMemo(() => [
    { value: 'low_stock', label: p('type_low_stock') },
    { value: 'high_stock', label: p('type_high_stock') },
    { value: 'reorder_point', label: p('type_reorder_point') },
    { value: 'out_of_stock', label: p('type_out_of_stock') },
  ], [p]);

  const severityOptions = useMemo(() => [
    { value: 'critical', label: p('severity_critical') },
    { value: 'high', label: p('severity_high') },
    { value: 'warning', label: p('severity_warning') },
    { value: 'info', label: p('severity_info') },
  ], [p]);

  const productOptions = useMemo(
    () => products.map((prod) => ({
      value: prod.id,
      label: prod.reference ? `${prod.name} (${prod.reference})` : prod.name || prod.id,
    })),
    [products],
  );

  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredAlerts, 'alertes_stock', [
      { key: 'alert_number', label: p('col_alert_number') },
      { key: 'product_name', label: p('col_product') },
      { key: 'current_stock', label: p('col_stock') },
      { key: 'severity', label: p('col_severity') },
      { key: 'status', label: p('col_status') }
    ]);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredAlerts;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_alert_number')}</th>
                <th>${p('col_product')}</th>
                <th>${p('export_col_current_stock')}</th>
                <th>${p('export_col_min_threshold')}</th>
                <th>${p('col_type')}</th>
                <th>${p('col_severity')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.alert_number || p('na')}</td>
                  <td>${item.product_name || p('na')}</td>
                  <td>${item.current_stock || 0}</td>
                  <td>${item.minimum_threshold || 0}</td>
                  <td>${item.alert_type || p('na')}</td>
                  <td>${item.severity || p('na')}</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('export_report_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('error_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredAlerts;
      
      const columns = [
        { key: 'alert_number', label: p('col_alert_number'), width: 20 },
        { key: 'product_name', label: p('col_product'), width: 25 },
        { key: 'product_code', label: p('export_col_product_code'), width: 15 },
        { key: 'current_stock', label: p('export_col_current_stock'), width: 12 },
        { key: 'minimum_threshold', label: p('export_col_min_threshold'), width: 12 },
        { key: 'maximum_threshold', label: p('export_col_max_threshold'), width: 12 },
        { key: 'alert_type', label: p('col_type'), width: 15 },
        { key: 'severity', label: p('col_severity'), width: 12 },
        { key: 'status', label: p('col_status'), width: 12 }
      ];
      
      const data = filteredData.map(item => ({
        alert_number: item.alert_number || '',
        product_name: item.product_name || '',
        product_code: item.product_code || '',
        current_stock: item.current_stock || 0,
        minimum_threshold: item.minimum_threshold || 0,
        maximum_threshold: item.maximum_threshold || 0,
        alert_type: item.alert_type || '',
        severity: item.severity || '',
        status: item.status || ''
      }));
      
      await generateReportExcel('stock_alerts_report', p('export_report_title'), {
        sheetName: p('export_sheet_name'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('error_excel'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: Bell, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'acknowledged', label: p('status_acknowledged'), icon: CheckCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'resolved', label: p('status_resolved'), icon: CheckSquare, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'dismissed', label: p('status_dismissed'), icon: BellOff, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/stock-alerts/${recordId}`, { status: newStatus });
      await loadAlerts();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };


  const getSeverityBadge = (severity) => {
    const severities = {
      critical: { label: p('severity_critical'), color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
      high: { label: p('severity_high'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertCircle },
      warning: { label: p('severity_warning'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: AlertCircle },
      info: { label: p('severity_info'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: AlertCircle }
    };
    const config = severities[severity] || severities.warning;
    const Icon = config.icon;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color} flex items-center gap-1`}><Icon size={12} />{config.label}</span>;
  };

  const getAlertTypeBadge = (alertType) => {
    const types = {
      low_stock: { label: p('type_low_stock'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      high_stock: { label: p('type_high_stock'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      reorder_point: { label: p('type_reorder_point'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      out_of_stock: { label: p('type_out_of_stock'), color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
    };
    const config = types[alertType] || types.low_stock;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>{config.label}</span>;
  };

  const getStockComparison = (current, threshold) => {
    const currentStock = parseFloat(current) || 0;
    const minThreshold = parseFloat(threshold) || 0;
    const criticalThreshold = parseFloat(inventoryConfig.criticalStockThreshold) || 0;

    if (currentStock === 0) {
      return <span className="text-red-400 font-medium text-sm">{p('stock_out_total')}</span>;
    }

    if (criticalThreshold > 0 && currentStock <= criticalThreshold) {
      return <span className="text-red-400 font-medium text-sm">{p('stock_percent_threshold', { percent: 0 })}</span>;
    }
    
    const percentage = minThreshold > 0 ? ((currentStock / minThreshold) * 100).toFixed(0) : 0;
    
    if (percentage <= 25) {
      return <span className="text-red-400 font-medium text-sm">{p('stock_percent_threshold', { percent: percentage })}</span>;
    } else if (percentage <= 50) {
      return <span className="text-orange-400 font-medium text-sm">{p('stock_percent_threshold', { percent: percentage })}</span>;
    } else {
      return <span className="text-yellow-400 font-medium text-sm">{p('stock_percent_threshold', { percent: percentage })}</span>;
    }
  };

  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'alert_number', labelKey: 'col_alert_number' },
    { key: 'product_name', labelKey: 'col_product' },
    { key: 'current_stock', labelKey: 'col_current_stock' },
    { key: 'alert_type', labelKey: 'col_type' },
    { key: 'severity', labelKey: 'col_severity' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: AlertTriangle },
    { key: 'stat_active', label: p('stat_active'), value: stats.active, icon: AlertCircle },
    { key: 'stat_critical', label: p('stat_critical'), value: stats.critical, icon: XCircle },
    { key: 'stat_acknowledged', label: p('stat_acknowledged'), value: stats.acknowledged, icon: CheckCircle },
  ], 4), [stats, p]);

  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, () => loadAlerts());
    });
  };

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={stockAlertsDisabled ? p('notice_alerts_disabled_banner', { defaultValue: 'تنبيهات المخزون المنخفض والفائض معطّلة — فعّلها من إعدادات المخزون ← إعادة الطلب.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={AlertTriangle}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
      onCreate={openNewAlertModal}
      createLabel={p('new_alert')}
      importExport={importExportProps}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('alert_number') && (

                  
                    <TableColumnHeaderFilter columnKey="alert_number" label={p('col_alert_number')} value={colFilters.alert_number ?? ''} onChange={(v) => setColFilter('alert_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('product_name') && (

                    <TableColumnHeaderFilter columnKey="product_name" label={p('col_product')} value={colFilters.product_name ?? ''} onChange={(v) => setColFilter('product_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('current_stock') && (

                    <TableColumnHeaderFilter columnKey="current_stock" label={p('col_current_stock')} value={colFilters.current_stock ?? ''} onChange={(v) => setColFilter('current_stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('alert_type') && (

                    <TableColumnHeaderFilter columnKey="alert_type" label={p('col_type')} value={colFilters.alert_type ?? ''} onChange={(v) => setColFilter('alert_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('severity') && (

                    <TableColumnHeaderFilter columnKey="severity" label={p('col_severity')} value={colFilters.severity ?? ''} onChange={(v) => setColFilter('severity', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleAlerts.length > 0 ? (
                  visibleAlerts.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      
                      {isColVisible('alert_number') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <Bell size={16} className="text-red-400" />
                            {record.alert_number}
                          </span>
                          {record.triggered_date && <span className="text-white/50 text-xs mt-0.5">{formatDate(record.triggered_date)}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('product_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.product_name || p('na')}</span>
                          {record.product_code && <span className="text-white/50 text-xs mt-0.5">{p('code_label')} {record.product_code}</span>}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('current_stock') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-white font-semibold">{record.current_stock || 0}</span>
                          <span className="text-white/50 text-xs">{p('threshold_label')} {record.minimum_threshold || 0}</span>
                          {getStockComparison(record.current_stock, record.minimum_threshold)}
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('alert_type') && (

                      
                        <td className="px-6 py-4">{getAlertTypeBadge(record.alert_type)}</td>

                      
                      )}
                      
                      {isColVisible('severity') && (

                      
                        <td className="px-6 py-4">{getSeverityBadge(record.severity)}</td>

                      
                      )}
                      
                      {isColVisible('status') && (

                      
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      
                      )}
                      
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            viewTitle={p('view')}
                          />
                          
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Bell size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleAlerts.length > 0 ? (
              visibleAlerts.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Bell size={20} className="text-red-400" />
                          {record.alert_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        {getSeverityBadge(record.severity)}
                        {getAlertTypeBadge(record.alert_type)}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_product')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.product_name || p('na')}</div>
                            <div><span className="font-medium">{p('code_label')}</span> {record.product_code || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_stock')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_current')}</span> {record.current_stock || 0}</div>
                            <div><span className="font-medium">{p('label_min')}</span> {record.minimum_threshold || 0}</div>
                            <div>{getStockComparison(record.current_stock, record.minimum_threshold)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.triggered_date && <div><span className="font-medium">{p('label_triggered')}</span> {formatDate(record.triggered_date)}</div>}
                            {record.acknowledged_date && <div><span className="font-medium">{p('label_acknowledged_date')}</span> {formatDate(record.acknowledged_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_action')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.action_taken || p('no_action')}</p>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Bell size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      
      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_title') : p('new_alert')}
          icon={Bell}
          formId="alert-form"
          saveLabel={t('common.save')}
          saving={saving}
          maxWidth="900px"
          testId="alert-crud-modal"
        >
          <form id="alert-form" onSubmit={handleSubmit}>
            <InventaireFormTabBar
              tabs={alertFormTabs}
              activeTab={alertFormTab}
              onTabChange={setAlertFormTab}
              isLight={isLight}
              ariaLabel={editingRecord ? p('modal_title') : p('new_alert')}
            />
            {alertFormTab === 'productStock' && (
            <div className={`${INVENTAIRE_FORM_GRID} mt-4`}>
              <div className="md:col-span-3">
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_product')}</label>
                <CustomSelect
                  value={formData.product_id}
                  onChange={handleProductSelect}
                  options={productOptions}
                  placeholder={p('label_product')}
                  searchable
                  creatable
                />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_current_stock')}</label>
                <CustomNumberInput value={formData.current_stock} onChange={(val) => setFormData({ ...formData, current_stock: val })} placeholder="0" />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_min_threshold')}</label>
                <CustomNumberInput value={formData.minimum_threshold} onChange={(val) => setFormData({ ...formData, minimum_threshold: val })} placeholder="0" />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_max_threshold')}</label>
                <CustomNumberInput value={formData.maximum_threshold} onChange={(val) => setFormData({ ...formData, maximum_threshold: val })} placeholder="0" />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_reorder_point')}</label>
                <CustomNumberInput value={formData.reorder_point} onChange={(val) => setFormData({ ...formData, reorder_point: val })} placeholder="0" />
              </div>
            </div>
            )}
            {alertFormTab === 'alertConfig' && (
            <div className={`${INVENTAIRE_FORM_GRID} mt-4`}>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_type')}</label>
                <CustomSelect value={formData.alert_type} onChange={(val) => setFormData({ ...formData, alert_type: val })} options={alertTypeOptions} placeholder={p('col_type')} />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_severity')}</label>
                <CustomSelect value={formData.severity} onChange={(val) => setFormData({ ...formData, severity: val })} options={severityOptions} placeholder={p('col_severity')} />
              </div>
              <div>
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('col_status')}</label>
                <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} placeholder={p('col_status')} />
              </div>
              <div className="md:col-span-3">
                <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>{p('label_notes')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className={cfgTheme.input} placeholder={p('no_notes')} />
              </div>
            </div>
            )}
          </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('modal_title')}
          icon={Bell}
          maxWidth="1100px"
          testId="alert-view-modal"
        >
          <div className="space-y-4">
            <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
              <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                <Bell size={18} className="text-red-400" />
                {p('section_identification')}
              </h3>
              <div className={INVENTAIRE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_alert_number')}</p>
                  <p className={`font-medium text-lg ${cfgTheme.title}`}>{viewingRecord.alert_number}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_status')}</p>
                  <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_severity')}</p>
                  <div className="flex">{getSeverityBadge(viewingRecord.severity)}</div>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('col_type')}</p>
                  <div className="flex">{getAlertTypeBadge(viewingRecord.alert_type)}</div>
                </div>
              </div>
            </div>

            <div className={INVENTAIRE_FORM_GRID}>
              <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
                <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                  <Package size={18} className="text-green-400" />
                  {p('section_product_stock')}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_product')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.product_name || p('na')}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_current_stock')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.current_stock || 0}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_comparison')}</p>
                    <div className="flex">{getStockComparison(viewingRecord.current_stock, viewingRecord.minimum_threshold)}</div>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
                <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                  <Gauge size={18} className="text-purple-400" />
                  {p('section_thresholds')}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_min_threshold')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.minimum_threshold || 0}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_max_threshold')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.maximum_threshold || 0}</p>
                  </div>
                  <div>
                    <p className={`text-sm mb-1 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_reorder_point')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.reorder_point || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={`rounded-xl p-5 border ${isLight ? 'border-slate-200 bg-slate-50' : 'border-white/20 bg-white/5'}`}>
              <h3 className={`font-semibold mb-4 flex items-center gap-2 ${cfgTheme.title}`}>
                <FileText size={18} className="text-yellow-400" />
                {p('section_notes_action')}
              </h3>
              <div className="space-y-2">
                <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_action_taken')}</p>
                <p className={isLight ? 'text-slate-800' : 'text-white/80'}>{viewingRecord.action_taken || p('no_action')}</p>
                <p className={`text-sm mt-4 ${isLight ? 'text-slate-500' : 'text-white/60'}`}>{p('label_notes')}</p>
                <p className={isLight ? 'text-slate-800' : 'text-white/80'}>{viewingRecord.notes || p('no_notes')}</p>
              </div>
            </div>
          </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default AlertesSeuilsStockPage;
